<template>
  <v-menu
    :data-testid="`${testIdPrefix}__profile-menu`"
    :value="value"
    @input="$emit('input', $event)"
    offset-x
    :nudge-right="0"
    transition="fade-transition"
    :close-on-content-click="closeOnContentClick"
    :open-delay="hoverOpenDelay"
    :close-delay="hoverCloseDelay"
    :content-class="'profile-inline-menu ' + contentClass"
    open-on-hover
  >
    <template #activator="{ on, attrs }">
      <v-list-item
        :data-testid="`${testIdPrefix}__profile-activator`"
        :class="activatorClasses"
        v-bind="attrs"
        v-on="on"
        :aria-label="activatorAriaLabel"
        @click="handleActivatorClick"
      >
        <template v-if="mini">
          <v-tooltip
            :data-testid="`${testIdPrefix}__profile-tooltip`"
            right
            content-class="tooltip-right"
          >
            <template #activator="{ on: ton, attrs: tattrs }">
              <div
                :data-testid="`${testIdPrefix}__profile-avatar-wrapper`"
                class="d-flex align-center justify-center w-100"
                v-bind="tattrs"
                v-on="ton"
              >
                <v-avatar
                  :data-testid="`${testIdPrefix}__profile-avatar`"
                  size="22"
                  color=""
                  class="avatar-img"
                >
                    <template v-if="user.profile && !profileImageError">
                      <img
                        :data-testid="`${testIdPrefix}__profile-image`"
                        :src="user.profile"
                        alt="profile"
                        @error="onAvatarError"
                      />
                    </template>
                    <template v-else>
                      <span
                        :data-testid="`${testIdPrefix}__profile-initial`"
                        class="font-weight-regular"
                      >{{ fallbackInitial }}</span>
                    </template>
                </v-avatar>
              </div>
            </template>
            <span
              :data-testid="`${testIdPrefix}__profile-tooltip-text`"
            >Profile</span>
          </v-tooltip>
        </template>
        <template v-else>
          <v-list-item-icon
            :data-testid="`${testIdPrefix}__profile-icon`"
            class="mr-2 ma-0 d-flex align-center justify-center align-self-center"
          >
            <v-avatar
              :data-testid="`${testIdPrefix}__profile-avatar`"
              size="22"
              color=""
              class="avatar-img mr-2"
            >
              <template v-if="user.profile && !profileImageError">
                <img
                  :data-testid="`${testIdPrefix}__profile-image`"
                  :src="user.profile"
                  alt="profile"
                  @error="onAvatarError"
                />
              </template>
                <template v-else>
                  <span
                    :data-testid="`${testIdPrefix}__profile-initial`"
                    class="font-weight-regular"
                  >{{ fallbackInitial }}</span>
                </template>
            </v-avatar>
            <div
              :data-testid="`${testIdPrefix}__profile-text-wrapper`"
              class="d-flex flex-column hide-when-mini"
            >
              <p
                :data-testid="`${testIdPrefix}__profile-text`"
                class="side-nav-text-color"
                style="max-width:140px"
              >Profile</p>
            </div>
          </v-list-item-icon>
          <img
            v-if="!mini"
            src="/img/navicon/LeftArrow.svg"
            class="left-arrow-icon"
            alt="Left arrow for Profile"
          />
        </template>
      </v-list-item>
    </template>
    <v-card
      :data-testid="`${testIdPrefix}__profile-card`"
      flat
      class="pa-0"
      width="240"
    >
      <v-list :data-testid="`${testIdPrefix}__profile-list`" class="d-flex flex-column gap-2">
        <v-list-item :data-testid="`${testIdPrefix}__profile-header`">
          <v-list-item-content
            :data-testid="`${testIdPrefix}__profile-content`"
            class="text-center justify-center pa-0 gap-1"
          >
            <v-avatar
              :data-testid="`${testIdPrefix}__profile-large-avatar`"
              size="32"
              color=""
              class="mb-2 flex-none avatar-img"
            >
              <template v-if="user.profile && !profileImageError">
                <img
                  :data-testid="`${testIdPrefix}__profile-large-image`"
                  :src="user.profile"
                  alt="profile"
                  @error="onAvatarError"
                />
              </template>
              <h4
                v-else
                :data-testid="`${testIdPrefix}__profile-large-initials`"
                class="font-weight-regular"
              >{{ fallbackInitial }}</h4>
            </v-avatar>
            <v-list-item-title
              :data-testid="`${testIdPrefix}__profile-name`"
              class="font-weight-semibold white--text"
            >{{ user.name }}</v-list-item-title>
            <v-list-item-subtitle
              :data-testid="`${testIdPrefix}__profile-email`"
              class="textLowercase white--text"
            >
              <span>{{ user.email }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          :data-testid="`${testIdPrefix}__edit-profile-button`"
          class="editProfile-btn"
          @click="handleEditProfile"
        >
          <v-list-item-title
            :data-testid="`${testIdPrefix}__edit-profile-text`"
            class="text-center"
          >Edit Profile</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: 'ProfileMenuFlyout',
  props: {
    value: { type: Boolean, default: false },
    mini: { type: Boolean, default: false },
    hoverCloseDelay: { type: Number, default: 300 },
    hoverOpenDelay: { type: Number, default: 0 },
    contentClass: { type: String, default: '' },
    closeOnContentClick: { type: Boolean, default: false },
    activatorExtraClass: { type: [String, Array, Object], default: '' },
    activatorAriaLabel: { type: String, default: null },
    openOnClick: { type: Boolean, default: false },
    testIdPrefix: { type: String, required: true }
  },
  data() {
    return {
      profileImageError: false
    };
  },
  computed: {
    user() {
      return this.$store?.state?.user || {};
    },
    fallbackInitial() {
      const initial = this.user?.name ? this.user.name.charAt(0) : 'U';
      return initial.toUpperCase();
    },
    activatorClasses() {
      const base = 'd-flex align-center justify-space-between cursor-pointer nav-standard-item menu-trigger-item';
      if (!this.activatorExtraClass) return base;
      return [base, this.activatorExtraClass];
    }
  },
  methods: {
    handleActivatorClick() {
      if (this.openOnClick) {
        this.$emit('input', true);
      }
    },
    handleEditProfile() {
      this.$emit('edit-profile');
      this.$emit('input', false);
    },
    onAvatarError() {
      this.profileImageError = true;
    }
  },
  watch: {
    value(val) {
      if (!val) {
        this.profileImageError = false;
      }
    }
  }
};
</script>

<style scoped>
  .avatar-img {
    object-fit: cover;
    background-color: var(--color-primary-base);
    border: 1px solid var(--grey-white);
    color: var(--grey-white);
  }
  .editProfile-btn{
    cursor: pointer;
    padding: var(--padding-xs, 8px);
    border-radius: var(--radius-xs);
    background: var(--grey-100, #E5E5E5);
  }
</style>